import {TextInput, TextInputHidden, SquareButton, HeaderBig, LoadButton, NavigateSmart, LinkSmart, DropdownMenu} from '../common/Common';
import { useContext, useEffect, useState } from 'react';
import './Register.css';
import {checkEmail, getDisplayLanguage, get_translation_objects, postWithCsrf, setLoginStatesFromJson, setLoginStatesFromJsonConsumer} from '../common/util';
import { LocaleContext, LoginContext, OverlayContext, ScrollContext, StateContext } from '../common/App';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { LoadState, Location, UserType } from '../common/enums.mjs';
import { maxPasswordLength, maxUsernameLength } from '../common/globals_common.mjs';
import { ConsumerContext } from '..';
import { useIndexUpdate, useNavigateSmart, useReCaptcha } from '../common/hooks';

export function Register(props) {
    const [email, setEMail] = useState("");
    const [password, setPassword] = useState("");
    const [passwordConfirmation, setPasswordConfirmation] = useState("");
    const [conditionsAccepted, setConditionsAccepted] = useState(false);
    const [newsletterAccepted, setNewsletterAccepted] = useState(false);
    const [errorText, setErrorText] = useState(null);
    const [loadState, setLoadState] = useState(LoadState.NONE);

    const state = useContext(StateContext);
    const locale = useContext(LocaleContext);
    const login = useContext(LoginContext);
    const consumer = useContext(ConsumerContext);
    const scroll = useContext(ScrollContext);
    const overlay = useContext(OverlayContext);

    const navigate = useNavigateSmart();
    const [searchParams, setSearchParams] = useSearchParams();

    const fetchReCaptchaToken = useReCaptcha();


    // FUNCTIONS:

    function handleConditionsChange(e) {
        setConditionsAccepted(e.target.checked);
    }

    function checkInputForButton() {
        if(!email || !password) {
            return false;
        }

        if(email === "" || password === ""){
            return false;
        }

        if(!conditionsAccepted){
            return false;
        }

        if(password != passwordConfirmation){
            return false;
        }

        return true;
    }

    function handleSubmit(e) {
        e.preventDefault();

        if(checkInputForButton()){
            try {
                let displayError = true;

                fetchReCaptchaToken().then((token) => {
                    return postWithCsrf(
                        "/user/register",
                        JSON.stringify({email: email, password: password, location: props.registerInfo.location,
                                newsletter: newsletterAccepted, userType: props.registerInfo.userType, lang: getDisplayLanguage(locale),
                                recaptcha_token: token}),
                        state
                    ).then((response) => {
                        if(response.ok) {
                            return response.json();
                        }
                        else {
                            if(response.status == 521) {
                                return response.text().then((text) => {
                                    overlay.setErrorPopupInfo({"title": locale.msgs.REGISTER_ERROR, "text": text});
                                    overlay.setErrorPopupDisplayed(true);

                                    throw new Error("Response code: " + response.status);
                                });
                            }
                            else if(response.status == 527) {
                                return response.text().then(() => {
                                    overlay.setErrorPopupInfo({"title": locale.msgs.BOT_PROTECTION_ERROR, "text": locale.msgs.BOT_PROTECTION_ERROR_DESCR});
                                    overlay.setErrorPopupDisplayed(true);

                                    displayError = false;
                                    throw new Error("Response code: " + response.status);
                                });
                            }
                            else {
                                throw new Error("Response code: " + response.status);
                            }
                        }
                    }).then((json) => {
                        setLoginStatesFromJson(json, login);
                        setLoginStatesFromJsonConsumer(json, consumer);
                        setLoadState(LoadState.LOAD_SUCCESS);
                        navigate("/confirm_mail/");
                    });
                }).catch((error) => {
                    console.log("Register ERROR: " + error);

                    if(displayError) {
                        setLoadState(LoadState.LOAD_ERROR);

                        setErrorText(
                            <div className="text_error">
                                {locale.msgs.REGISTRATION_FAILED}&nbsp;
                                <LinkSmart onClick={() => scroll.scrollToPlain(0, 0)} className="text_error" to="/login/">{locale.msgs.GO_TO_LOGIN}</LinkSmart>
                            </div>
                        );
                    }
                    else {
                        setLoadState(LoadState.NONE);
                    }
                });

                // Calling setLoadState() after postWithCsrf() as it could throw an exception
                setLoadState(LoadState.LOADING);
                setErrorText("");
            }
            catch(e) {
                console.log(e);
            }
        }
    }


    // EFFECTS:

    useEffect(() => {
        setErrorText("");
    }, [email, password]);

    useEffect(() => {
        if(!props.registerInfo.userType ||!props.registerInfo.location) {
            navigate("/register/");
        }
    }, [props.registerInfo]);

    useIndexUpdate(props.indexUpdate, locale);


    // RENDER:

    const [locationTransList, locationTransListInverse] = get_translation_objects("location_long", Location, locale);
    const [userTypeTransList, userTypeTransListInverse] = get_translation_objects("user_type", UserType, locale);

    const pwdPattern = '^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[\'!@#$%^&*()_+-=[]{}|;:,.<>?/~`"`´^°~\\])(?!^\d+$).{8,}$';
    let inputValid = checkInputForButton();

    return (
        <div className="bottom_padding margin_to_header" id="register" onSubmit={handleSubmit}>
            <h1 className="login_register_head text_big_big">{locale.msgs.REGISTER_LONG}</h1>

            { login.loggedIn ?
                <NavigateSmart to="/dashboard/" />
            :
                null
            }

            <form>
                <div className="register_first_input_box">
                    <DropdownMenu minimal={false} readOnly={false} text={locale.msgs.WHAT_ARE_YOU_LOOKING_FOR} value={userTypeTransList[props.registerInfo.userType]}
                            onValueChange={(val) => props.setRegisterInfo({...props.registerInfo, "userType": userTypeTransListInverse[val]})}
                            options={Object.values(userTypeTransList)} />
                </div>

                <div className="register_input_box">
                    <TextInput text={locale.msgs.EMAIL} name="email" type="email" value={email} autocomplete="new-email"
                            onChange={(value) => setEMail(value)}
                            checker={(v) => checkEmail(v)}
                            errorText={locale.msgs.ENTER_VALID_EMAIL_ADDRESS}
                            maxLength={maxUsernameLength}
                    />
                </div>

                <NewPasswordInput password={password} setPassword={setPassword}
                        passwordConfirmation={passwordConfirmation} setPasswordConfirmation={setPasswordConfirmation} />

                <div className="register_input_box">
                    <DropdownMenu minimal={false} readOnly={false} text={locale.msgs.MN_LOCATION} value={locationTransList[props.registerInfo.location]}
                            onValueChange={(val) => props.setRegisterInfo({...props.registerInfo, "location": locationTransListInverse[val]})}
                            options={Object.values(locationTransList)}
                            textSearch={true}
                            textSearchInstruction={locale.msgs.SEARCH_LOCATION_VIA_TEXT_INSTRUCTION} />
                </div>

                <div className="checkbox_box" id="conditions_box">
                    <input type="checkbox" name="conditions" id="conditions_checkbox" className="input_radio_check checkbox"
                            onChange={handleConditionsChange} checked={conditionsAccepted}></input>
                    <label className="noselect checkbox_label" htmlFor="conditions_checkbox">

                        {locale.msgs.ACCEPT_TOS_1}
                        <LinkSmart className="link_2" to={"/terms_of_use/"} target="_blank">{locale.msgs.TOS_2}</LinkSmart>
                        {locale.msgs.ACCEPT_TOS_2}
                        <LinkSmart className="link_2" to={"/privacy_policy_register/"} target="_blank">{locale.msgs.PRIVACY_POLICY_REGISTER}</LinkSmart>
                        {locale.msgs.ACCEPT_TOS_3}

                    </label>
                </div>

                <div className="checkbox_box" id="register_newsletter_box">
                    <input type="checkbox" name="register_newsletter" id="register_newsletter_checkbox" className="input_radio_check checkbox"
                            onChange={(e) => setNewsletterAccepted(e.target.checked)} checked={newsletterAccepted}></input>
                    <label className="noselect checkbox_label" htmlFor="register_newsletter_checkbox">
                        {locale.msgs.REGISTER_NEWSLETTER_DESCR}
                    </label>
                </div>

                <div id="register_btn_box">
                    <LoadButton white={false} text={locale.msgs.REGISTER}
                            clickable={inputValid}
                            saving={loadState === LoadState.LOADING}
                            onClick={(e) => {}}
                            notSaved={false}
                            saved={false}
                            sucText={""} errText={""}
                            submit={true}
                    />
                </div>

                <div className="text_small_small_2 register_more_info">
                    {locale.msgs.MORE_INFO_IN_GENERAL_PRIVACY_POLICY}
                    <LinkSmart className="link_2" to={"/privacy_policy/"} target="_blank">{locale.msgs.PRIVACY_POLICY}</LinkSmart>
                    .
                </div>
            </form>

            {errorText}
        </div>
    );
}

export const NewPasswordInput = ({password, setPassword, passwordConfirmation, setPasswordConfirmation}) => {
    const locale = useContext(LocaleContext);
    const [type, setType] = useState("password");

    const [checkTrigger, setCheckTrigger] = useState(false);
    const [checkTriggerOnlyErr, setCheckTriggerOnlyErr] = useState(false);

    return (
        <div>
            <div className="password_box">
                <TextInput type="password" text={locale.msgs.PASSWORD} id="reg-password" name="reg-password"
                        tempType={type} onTempTypeChange={setType}
                        value={password} autocomplete="new-password"
                        onChange={(value) => {
                            setPassword(value);

                            if(value) {
                                setCheckTriggerOnlyErr(!checkTriggerOnlyErr);
                            }
                            
                        }}
                        onBlur={() => {
                            setCheckTrigger(!checkTrigger);
                        }}
                        maxLength={maxPasswordLength}
                        checker={(value) => {
                            // Check for lowercase letter
                            if(!/[a-z]/.test(value)) {
                                return false;
                            }

                            // Check for uppercase letter
                            if(!/[A-Z]/.test(value)) {
                                return false;
                            }

                            // Check for digit
                            if(!/[0-9]/.test(value)) {
                                return false;
                            }

                            // Check for length
                            if(value.length < 8) {
                                return false;
                            }

                            // Check for special character
                            if(!/[^a-zA-Z0-9]/.test(value)) {
                                return false;
                            }

                            return true;
                        }}
                        errorText={ <div  dangerouslySetInnerHTML={{__html: locale.msgs.ENTER_VALID_PASSWORD }}></div>}
                />
            </div>

            <div className="password_box">
                <TextInput type="password" text={locale.msgs.REPEAT_PASSWORD} id="reg-password_confirm" name="reg-password_confirm"
                        tempType={type} onTempTypeChange={setType}
                        value={passwordConfirmation}
                        autocomplete="new-password" onChange={(value) => setPasswordConfirmation(value)}
                        maxLength={maxPasswordLength}
                        checker={(value) => {
                            return password === value || password === null;
                        }}
                        manualCheckTrigger={checkTrigger}
                        manualCheckTriggerOnlyErr={checkTriggerOnlyErr}
                        errorText={locale.msgs.PASSWORDS_DO_NOT_MATCH}
                />
            </div>
        </div>
    );
}
